// The KPI strips (D104). Every number is ListOccurrences' own state_counts
// (the named denominators, taken over the window before any state filter),
// never a client recount, and every tile links to the rows it counts.
//
//   - ProgramKpiStrip: the program's roll-up — cumulative over every contract
//     period by default, with a picker to narrow to one period (Base / OPn;
//     statuses still as of today).
//   - TeamKpiStrip: one team's own current window (its in-force SOW's
//     interval, else its exercise line's): the same five states, plus
//     "Awaiting approval (n)" (D112 #8), linking to that team's approvals list.
//     A picked contract period counts that period of the team instead (R6-4);
//     with no current window and none picked it counts open items, so only
//     the open states have tiles.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraEmptyState, SpecraEyebrow, SpecraLink, SpecraMetricStrip, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { OccurrenceWindow, useOccurrenceDecisions, useOccurrences } from '../hooks';
import { cmSpan, todayISO } from '../lib/format';
import { LATE_OR_APPROVED_LATE } from '../lib/status';
import { resolveOccurrencePeriodId } from '../lib/tableControls';
import { useCmLookups } from '../lookups';
import { awaitingHref, isDateWaiver, waiverStatusMatches } from './AwaitingApproval';
// Each tile counts one state; `info` says what that state means, and the
// strip appends the window it counts over (SAD-998).
const TILES = [
    {
        state: 'late',
        label: 'Late',
        tone: 'danger',
        info: 'Dates past due with nothing filed yet'
    },
    {
        state: 'awaiting_review',
        label: 'Awaiting review',
        tone: 'info',
        info: 'Filed and waiting for the reviewer’s decision'
    },
    {
        state: 'rejected',
        label: 'Rejected',
        tone: 'danger',
        info: 'Filings the reviewer rejected, waiting for a new filing'
    },
    {
        state: 'due_soon',
        label: 'Due next 14 days',
        tone: 'warning',
        info: 'Dates due in the next 14 days with nothing filed yet'
    },
    {
        state: 'approved',
        label: 'Approved',
        tone: 'success',
        info: 'Dates whose filing was approved'
    }
];
function hasAny(r) {
    return !!r && Object.values(r.stateCounts ?? {}).some((n)=>n > 0);
}
/**
 * A strip's props: one tile per state, each a router link to the rows it
 * counts. `states` narrows the five (the contract-wide view shows three).
 */ export function stripProps({ counts, hrefFor, testId, tileTestId, states, extra = [], windowText, infoFor }) {
    const n = (state)=>counts?.[state] ?? 0;
    const metrics = [
        ...(states ? states.flatMap((st)=>TILES.filter((t)=>t.state === st)) : TILES).map((t)=>({
                id: t.state,
                label: t.label,
                value: n(t.state),
                tone: n(t.state) > 0 ? t.tone : 'neutral',
                href: hrefFor(t.state),
                info: windowText ? `${infoFor?.[t.state] ?? t.info}, ${windowText}.` : `${infoFor?.[t.state] ?? t.info}.`
            })),
        ...extra
    ];
    return {
        'data-testid': testId,
        metrics,
        renderLink: renderRouterLink,
        getItemTestId: (m)=>m.id === 'awaiting_approval' ? Testids.cmWorkstream.awaitingApproval : tileTestId,
        getItemProps: (m)=>({
                'data-state': m.id
            })
    };
}
/** Placeholder tiles while a strip's counts load; no testids, so nothing reads them as counts. */ export const LOADING_STRIP = {
    metrics: [],
    loading: true
};
export function ProgramKpiStrip({ programId, periodId, onPeriodChange }) {
    const lk = useCmLookups(programId);
    const q = useOccurrences(programId, periodId ? {
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: periodId,
        pageSize: 1
    } : {
        window: OccurrenceWindow.ALL,
        pageSize: 1
    });
    const r = q.data;
    const today = todayISO();
    // The period labelled "(current)" in the picker: named only when every
    // period is dated, since an undated one could actually be the current one.
    const current = lk.periods.some((p)=>!p.start || !p.end) ? undefined : lk.periods.find((p)=>p.start && p.start <= today && (!p.end || today <= p.end));
    const viewing = periodId ? lk.periodById.get(periodId) : undefined;
    const ended = !!viewing?.end && viewing.end < today;
    const base = `/delivery/programs/${programId}/deliverables`;
    // Waiver tiles count exactly the rows the waivers list shows behind their link: date
    // waivers whose occurrence resolves to the picked period (none picked = every period)
    // and the list's status filter.
    const decisions = useOccurrenceDecisions(programId, {});
    const waiverPeriodId = periodId;
    const waiverCounts = (()=>{
        const occById = new Map((decisions.data?.occurrences ?? []).map((o)=>[
                o.id,
                o
            ]));
        const inPeriod = (d)=>{
            if (!waiverPeriodId) return true;
            const occ = occById.get(d.occurrenceId);
            return !!occ && resolveOccurrencePeriodId(occ, lk.periods ?? [], lk.exercises ?? []) === waiverPeriodId;
        };
        const inScope = (decisions.data?.decisions ?? []).filter((d)=>isDateWaiver(d) && inPeriod(d));
        const count = (status)=>inScope.filter((d)=>waiverStatusMatches(d.step, status)).length;
        return {
            pending: count('pending'),
            approved: count('approved')
        };
    })();
    const waiverHref = (status)=>`/delivery/programs/${programId}/waivers-tailoring?${waiverPeriodId ? `waiverPeriod=${waiverPeriodId}&` : ''}waiverStatus=${status}`;
    const waiverPeriod = lk.periodById.get(waiverPeriodId);
    const waiverWindow = waiverPeriod ? `in ${waiverPeriod.name} (${cmSpan(waiverPeriod.start, waiverPeriod.end)})` : 'in any period';
    const waiverTile = (id, label, value, tone, info, status)=>({
            id,
            label,
            value,
            tone: value > 0 ? tone : 'neutral',
            href: waiverHref(status),
            info
        });
    // Late here is the unfiled late dates plus those approved after their due date (judged by
    // the submission date); the server's on-time counts give the second number over the same
    // window. The tile links to its own list value, so ?state=late keeps meaning unfiled only.
    const approvedLate = Math.max(0, (r?.onTime?.timingKnown ?? 0) - (r?.onTime?.onTime ?? 0));
    const counts = {
        ...r?.stateCounts,
        late: (r?.stateCounts?.late ?? 0) + approvedLate
    };
    const hrefFor = (state)=>`${base}?state=${state === 'late' ? LATE_OR_APPROVED_LATE : state}&period=${periodId || 'all'}`;
    const windowText = !periodId ? 'across all contract periods' : viewing ? `in ${viewing.name} (${cmSpan(viewing.start, viewing.end)})` : 'in the selected period';
    const label = !periodId ? 'All periods' : viewing ? `${viewing.name} · ${cmSpan(viewing.start, viewing.end)}` : 'Selected period';
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "aria-label": "Deliverables",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraEyebrow, {
                        children: "Deliverables"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "caption",
                        tone: "muted",
                        children: label
                    }),
                    periodId && ended ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "neutral",
                        "data-testid": Testids.cmKpi.viewingPast,
                        children: [
                            "Viewing ",
                            viewing?.name,
                            " (ended)"
                        ]
                    }) : null,
                    lk.periods.length > 0 ? /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Contract period",
                        className: "ml-auto w-48",
                        value: periodId,
                        onChange: (e)=>onPeriodChange(e.target.value),
                        "data-testid": Testids.cmKpi.periodPicker,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "All periods (cumulative)"
                            }),
                            lk.periods.filter((p)=>p.start && p.end).map((p)=>/*#__PURE__*/ _jsx("option", {
                                    value: p.id,
                                    children: p.id === current?.id ? `${p.name} (current)` : p.name
                                }, p.id))
                        ]
                    }) : null
                ]
            }),
            q.isLoading ? /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                ...LOADING_STRIP
            }) : !hasAny(r) ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                size: "compact",
                "data-testid": Testids.cmKpi.empty,
                title: "No dates yet.",
                description: "Deliverable dates appear once a deliverable has a rule and the period or exercise it covers is on the contract.",
                action: /*#__PURE__*/ _jsx(SpecraLink, {
                    href: base,
                    renderLink: renderRouterLink,
                    children: "Open Deliverables"
                })
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                        ...stripProps({
                            counts,
                            windowText,
                            hrefFor,
                            infoFor: {
                                late: 'Dates past due with nothing filed yet, plus dates approved after their due date'
                            },
                            testId: Testids.cmKpi.strip,
                            tileTestId: Testids.cmKpi.tile,
                            states: [
                                'late',
                                'approved',
                                'due_soon'
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                        ...stripProps({
                            counts: r?.stateCounts,
                            windowText,
                            hrefFor,
                            testId: Testids.cmKpi.strip,
                            tileTestId: Testids.cmKpi.tile,
                            states: [
                                'awaiting_review'
                            ],
                            extra: [
                                waiverTile('pending_waivers', 'Pending waivers', waiverCounts.pending, 'warning', `Counts waivers and extensions on dates ${waiverWindow} that are proposed but not yet approved.`, 'pending'),
                                waiverTile('approved_waivers', 'Approved waivers', waiverCounts.approved, 'success', `Counts waivers and extensions on dates ${waiverWindow} that have been approved.`, 'approved')
                            ]
                        }),
                        "data-testid": undefined
                    })
                ]
            }),
            periodId && r?.window?.basis === 'none' ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.cmKpi.noPeriod,
                children: "No contract period covers today; showing open items."
            }) : null,
            periodId && r && r.carriedOver > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.cmKpi.carriedOver,
                children: [
                    "Includes ",
                    r.carriedOver,
                    " open ",
                    r.carriedOver === 1 ? 'item' : 'items',
                    " carried over from an earlier period."
                ]
            }) : null
        ]
    });
}
/**
 * One team's strip over its own current window: the strip's props (or the
 * loading tiles) and the line naming that window. The team page hands the
 * props to its dashboard shell as its metrics.
 */ export function useTeamKpi(programId, workstreamId, { enabled = true, periodId = '' } = {}) {
    const lk = useCmLookups(programId);
    const q = useOccurrences(programId, periodId ? {
        workstreamId,
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: periodId,
        pageSize: 1
    } : {
        workstreamId,
        window: OccurrenceWindow.WORKSTREAM_CURRENT_WINDOW,
        pageSize: 1
    }, {
        enabled
    });
    const pending = useOccurrenceDecisions(programId, {
        workstreamId,
        openOnly: true
    }, {
        enabled
    });
    const n = pending.data?.decisions.length ?? 0;
    const base = `/delivery/programs/${programId}/workstreams/${workstreamId}`;
    const r = q.data;
    const picked = periodId ? lk.periodById.get(periodId) : undefined;
    // Open items only: nothing approved or due soon is in them to count.
    const openItems = !periodId && r?.window?.basis === 'none';
    const strip = q.isLoading ? LOADING_STRIP : stripProps({
        counts: r?.stateCounts,
        hrefFor: (state)=>`${base}?state=${state}`,
        windowText: picked ? `in ${picked.name} (${cmSpan(picked.start, picked.end)}), including open items carried over` : r?.window && r.window.basis !== 'none' ? `in this team’s window (${cmSpan(r.window.startOn, r.window.endOn)}), including open items carried over` : 'in this team’s open items',
        testId: Testids.cmWorkstream.strip,
        tileTestId: Testids.cmWorkstream.stripTile,
        states: openItems ? [
            'late',
            'awaiting_review',
            'rejected'
        ] : undefined,
        extra: [
            {
                id: 'awaiting_approval',
                label: 'Awaiting approval',
                value: n,
                tone: n > 0 ? 'warning' : 'neutral',
                note: 'Proposals a second person must approve',
                info: 'Proposed changes to this team’s dates that wait for a second person’s approval.',
                href: awaitingHref(programId, {
                    team: workstreamId
                })
            }
        ]
    });
    const windowNote = picked ? /*#__PURE__*/ _jsxs(SpecraText, {
        as: "p",
        size: "caption",
        tone: "muted",
        children: [
            "Showing ",
            picked.name,
            ": ",
            cmSpan(picked.start, picked.end),
            r && r.carriedOver > 0 ? ` · ${r.carriedOver} open carried over` : ''
        ]
    }) : r?.window && r.window.basis !== 'none' ? /*#__PURE__*/ _jsxs(SpecraText, {
        as: "p",
        size: "caption",
        tone: "muted",
        children: [
            "This team’s window: ",
            cmSpan(r.window.startOn, r.window.endOn),
            r.carriedOver > 0 ? ` · ${r.carriedOver} open carried over` : ''
        ]
    }) : r?.window?.basis === 'none' ? /*#__PURE__*/ _jsx(SpecraText, {
        as: "p",
        size: "caption",
        tone: "muted",
        children: "No current window: showing this team’s open items. Pick a period to see its filed and approved dates."
    }) : null;
    return {
        strip,
        windowNote
    };
}
export function TeamKpiStrip({ programId, workstreamId }) {
    const { strip, windowNote } = useTeamKpi(programId, workstreamId);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                ...strip
            }),
            windowNote
        ]
    });
}
